/* src/contexts/NotificationContext.js */
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, orderBy, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useProfile } from './ProfileContext'; // To get authUser
import { db } from '../firebaseConfig';

const functions = getFunctions(); // Get functions instance
const markNotificationReadFn = httpsCallable(functions, 'markNotificationRead');
const markAllNotificationsReadFn = httpsCallable(functions, 'markAllNotificationsRead');

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { authUser } = useProfile(); // Use ProfileContext to get user
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Effect to listen for notifications in Firestore
    useEffect(() => {
        let unsubscribe = () => {}; // Placeholder for cleanup function

        if (authUser?.uid) {
            setIsLoading(true);
            setError(null);
            console.log(`NotificationContext: Setting up listener for user ${authUser.uid}`);

            const notificationsRef = collection(db, 'users', authUser.uid, 'notifications');
            const q = query(notificationsRef, orderBy('createdAt', 'desc')); // Order by most recent

            unsubscribe = onSnapshot(q, (snapshot) => {
                const loadedNotifications = snapshot.docs.map(doc => {
                    const data = doc.data();
                    // Convert Firestore Timestamps to ISO strings for consistency
                    const createdAt = data.createdAt instanceof Timestamp
                        ? data.createdAt.toDate().toISOString()
                        : data.createdAt; // Keep original if conversion fails
                    return {
                        id: doc.id,
                        ...data,
                        createdAt: createdAt
                    };
                });

                setNotifications(loadedNotifications);
                const count = loadedNotifications.filter(n => !n.read).length;
                setUnreadCount(count);
                setIsLoading(false);
                console.log(`NotificationContext: Received ${loadedNotifications.length} notifications, ${count} unread.`);

            }, (err) => {
                console.error("Error fetching notifications:", err);
                setError("Failed to load notifications.");
                setNotifications([]);
                setUnreadCount(0);
                setIsLoading(false);
            });

        } else {
            // No user, reset state
            console.log("NotificationContext: No user, resetting state.");
            setNotifications([]);
            setUnreadCount(0);
            setIsLoading(false);
            setError(null);
        }

        // Cleanup listener on unmount or user change
        return () => {
            console.log("NotificationContext: Cleaning up listener.");
            unsubscribe();
        };
    }, [authUser]); // Re-run when user changes

    // Action to mark a single notification as read
    const markNotificationRead = useCallback(async (notificationId) => {
        if (!authUser?.uid) return;
        // Optimistic UI update (optional but good UX)
        setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));

        try {
            await markNotificationReadFn({ notificationId });
            // Success: State already updated optimistically
        } catch (error) {
            console.error("Failed to mark notification read:", error);
            // Revert optimistic update on error (optional)
            setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: false } : n));
            setUnreadCount(prev => prev + 1); // Crude revert, better to recalculate
            // setError("Failed to update notification status."); // Maybe show transient error
        }
    }, [authUser]);

    // Action to mark all notifications as read
    const markAllNotificationsRead = useCallback(async () => {
        if (!authUser?.uid || unreadCount === 0) return;
        // Optimistic UI update
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);

        try {
            await markAllNotificationsReadFn();
            // Success: State updated
        } catch (error) {
            console.error("Failed to mark all notifications read:", error);
            // Revert optimistic update (complex, easier to rely on listener refresh)
            // setError("Failed to update all notifications."); // Maybe show transient error
        }
    }, [authUser, unreadCount]);

    const value = {
        notifications,
        unreadCount,
        isLoading,
        error,
        markNotificationRead,
        markAllNotificationsRead,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};